/**
 * Comprehensive Nutrition Review Generator
 *
 * A deep-dive analysis that surfaces:
 * - Neo4j behavioral patterns (temporal, food combinations, gateway foods)
 * - 4-week rolling analysis with deficiencies and recommendations
 * - AI-generated summary with actionable insights
 */

import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

interface ComprehensiveReviewGeneratorProps {
  user: any;
  onBack: () => void;
  coachMode?: boolean;
  clientId?: number;
}

interface TemporalPattern {
  dayOfWeek: string;
  avgCalories: number;
  avgProtein: number;
  mealCount: number;
  insight: string;
}

interface FoodCombination {
  foods: string[];
  frequency: number;
  insight: string;
}

interface GatewayFood {
  food: string;
  nutrient: string;
  avgAmount: number;
  timesEaten: number;
  contribution: string;
}

interface RedFlag {
  date: string;
  issue: string;
  severity: 'warning' | 'critical';
}

interface Deficiency {
  nutrient: string;
  displayName: string;
  current: number;
  target: number;
  percentage: number;
  severity: 'mild' | 'moderate' | 'critical';
  unit: string;
  gatewayFoods: string[];
  recommendedFoods: string[];
  educationalSnippets: string[];
}

interface Patterns {
  temporalPatterns: TemporalPattern[];
  foodCombinations: FoodCombination[];
  gatewayFoods: GatewayFood[];
  topFoods: { food: string; frequency: number }[];
  redFlags: RedFlag[];
}

interface ComprehensiveReview {
  reviewId: string;
  period: {
    start: string;
    end: string;
    daysLogged: number;
    totalMeals: number;
  };
  overview: {
    avgDailyCalories: number;
    avgDailyProtein: number;
    avgDailyCarbs: number;
    avgDailyFat: number;
    avgDailyFiber: number;
    daysLogged: number;
    totalMeals: number;
    mealsPerDay: string;
  };
  patterns: Patterns;
  deficiencies: Deficiency[];
  summary: string;
  generatedAt: string;
}

export const ComprehensiveReviewGenerator: React.FC<ComprehensiveReviewGeneratorProps> = ({
  user,
  onBack,
  coachMode,
  clientId
}) => {
  const [generatedReview, setGeneratedReview] = useState<ComprehensiveReview | null>(null);
  const [activeSection, setActiveSection] = useState<'overview' | 'patterns' | 'deficiencies' | 'summary'>('overview');

  // Fetch past reviews
  const { data: pastReviews } = useQuery({
    queryKey: coachMode ? ['comprehensive-reviews', 'client', clientId] : ['comprehensive-reviews', user?.id],
    queryFn: async () => {
      const url = coachMode && clientId
        ? `${process.env.REACT_APP_API_URL}/api/reports/comprehensive-history?userId=${clientId}`
        : `${process.env.REACT_APP_API_URL}/api/reports/comprehensive-history`;

      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch reviews');
      const data = await response.json();
      return data.reviews;
    },
    enabled: coachMode ? !!clientId : !!user?.id
  });

  // Generate new review mutation
  const generateMutation = useMutation({
    mutationFn: async () => {
      const requestBody: any = {};
      if (coachMode && clientId) {
        requestBody.userId = clientId;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/reports/generate-comprehensive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate review');
      }

      const data = await response.json();
      return data.review;
    },
    onSuccess: (review) => {
      setGeneratedReview(review);
      setActiveSection('overview');
    }
  });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'moderate': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'mild': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium mb-2"
          >
            ← Back to Reports
          </button>
          <h2 className="text-2xl font-bold text-gray-900">Comprehensive Nutrition Review</h2>
          <p className="text-gray-600 mt-1">
            4-week deep analysis with behavioral patterns and personalized recommendations
          </p>
        </div>
      </div>

      {/* Generator Card */}
      {!generatedReview && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Generate New Review</h3>
          </div>
          <div className="card-body">
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                <span className="text-3xl">🔬</span>
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">Comprehensive Analysis</h4>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                This review analyzes 4 weeks of your nutrition data to discover eating patterns,
                identify deficiencies, and provide personalized food recommendations.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-left max-w-2xl mx-auto">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="text-blue-600 font-medium mb-1">📊 Behavioral Patterns</div>
                  <p className="text-sm text-gray-600">Day-of-week trends, food combinations, and eating habits</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="text-green-600 font-medium mb-1">🎯 Deficiency Analysis</div>
                  <p className="text-sm text-gray-600">Nutrient gaps with specific food recommendations</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <div className="text-purple-600 font-medium mb-1">💡 AI Insights</div>
                  <p className="text-sm text-gray-600">Personalized summary with actionable next steps</p>
                </div>
              </div>

              <button
                onClick={() => generateMutation.mutate()}
                disabled={generateMutation.isPending}
                className="px-8 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {generateMutation.isPending ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Analyzing 4 weeks of data...
                  </span>
                ) : (
                  '🔬 Generate Comprehensive Review'
                )}
              </button>

              {generateMutation.isError && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-left max-w-md mx-auto">
                  <div className="text-red-800 font-medium">Error</div>
                  <div className="text-red-600 text-sm mt-1">
                    {generateMutation.error instanceof Error ? generateMutation.error.message : 'Unknown error'}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Generated Review Display */}
      {generatedReview && (
        <>
          {/* Review Header */}
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Your Comprehensive Review</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {formatDate(generatedReview.period.start)} - {formatDate(generatedReview.period.end)} •
                    {generatedReview.period.daysLogged} days logged • {generatedReview.period.totalMeals} meals
                  </p>
                </div>
                <button
                  onClick={() => setGeneratedReview(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  Generate New
                </button>
              </div>
            </div>

            {/* Section Tabs */}
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px">
                {(['overview', 'patterns', 'deficiencies', 'summary'] as const).map((section) => (
                  <button
                    key={section}
                    onClick={() => setActiveSection(section)}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeSection === section
                        ? 'border-purple-500 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {section === 'overview' && '📊 Overview'}
                    {section === 'patterns' && '🔍 Patterns'}
                    {section === 'deficiencies' && '🎯 Deficiencies'}
                    {section === 'summary' && '💡 AI Summary'}
                  </button>
                ))}
              </nav>
            </div>

            <div className="card-body">
              {/* Overview Section */}
              {activeSection === 'overview' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">
                        {generatedReview.overview.avgDailyCalories}
                      </div>
                      <div className="text-sm text-gray-600">Avg Calories/Day</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {generatedReview.overview.avgDailyProtein}g
                      </div>
                      <div className="text-sm text-gray-600">Avg Protein/Day</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">
                        {generatedReview.period.daysLogged}/28
                      </div>
                      <div className="text-sm text-gray-600">Days Logged</div>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <div className="text-2xl font-bold text-orange-600">
                        {generatedReview.overview.mealsPerDay}
                      </div>
                      <div className="text-sm text-gray-600">Meals/Day</div>
                    </div>
                  </div>

                  {/* Top Foods */}
                  {generatedReview.patterns.topFoods.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">🍽️ Most Frequently Eaten Foods</h4>
                      <div className="flex flex-wrap gap-2">
                        {generatedReview.patterns.topFoods.slice(0, 8).map((food, i) => (
                          <span key={i} className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                            {food.food} <span className="text-gray-500">({food.frequency}x)</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Red Flags */}
                  {generatedReview.patterns.redFlags.length > 0 && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <h4 className="font-medium text-red-800 mb-2">🚨 Areas of Concern</h4>
                      <ul className="space-y-1">
                        {generatedReview.patterns.redFlags.map((flag, i) => (
                          <li key={i} className="text-sm text-red-700">
                            {flag.date}: {flag.issue}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Patterns Section */}
              {activeSection === 'patterns' && (
                <div className="space-y-6">
                  {/* Temporal Patterns */}
                  {generatedReview.patterns.temporalPatterns.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">📅 Day-of-Week Patterns</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {generatedReview.patterns.temporalPatterns.map((pattern, i) => (
                          <div key={i} className="p-4 border border-gray-200 rounded-lg">
                            <div className="font-medium text-gray-900">{pattern.dayOfWeek}</div>
                            <div className="text-sm text-gray-600 mt-1">
                              {pattern.avgCalories} kcal • {pattern.avgProtein}g protein
                            </div>
                            <div className="text-xs text-purple-600 mt-2">{pattern.insight}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Food Combinations */}
                  {generatedReview.patterns.foodCombinations.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">🤝 Food Combinations You Love</h4>
                      <div className="space-y-2">
                        {generatedReview.patterns.foodCombinations.map((combo, i) => (
                          <div key={i} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                            <span className="font-medium text-green-800">
                              {combo.foods.join(' + ')}
                            </span>
                            <span className="text-sm text-green-600">{combo.insight}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Gateway Foods */}
                  {generatedReview.patterns.gatewayFoods.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">⭐ Your Nutrient Champions</h4>
                      <p className="text-sm text-gray-600 mb-3">
                        These foods from your diet provide significant amounts of key nutrients
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {generatedReview.patterns.gatewayFoods.map((gf, i) => (
                          <div key={i} className="p-4 border border-blue-200 bg-blue-50 rounded-lg">
                            <div className="font-medium text-blue-900">{gf.food}</div>
                            <div className="text-sm text-blue-700 mt-1">{gf.contribution}</div>
                            <div className="text-xs text-blue-600 mt-1">
                              Eaten {gf.timesEaten} times
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Deficiencies Section */}
              {activeSection === 'deficiencies' && (
                <div className="space-y-4">
                  {generatedReview.deficiencies.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-3">🎉</div>
                      <div className="text-gray-600">
                        No significant nutrient deficiencies detected!
                      </div>
                    </div>
                  ) : (
                    generatedReview.deficiencies.map((def, i) => (
                      <div key={i} className={`p-4 border rounded-lg ${getSeverityColor(def.severity)}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="font-medium">{def.displayName}</div>
                            <div className="text-sm mt-1">
                              {def.current} / {def.target} {def.unit} ({def.percentage}% of target)
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            def.severity === 'critical' ? 'bg-red-200' :
                            def.severity === 'moderate' ? 'bg-orange-200' : 'bg-yellow-200'
                          }`}>
                            {def.severity}
                          </span>
                        </div>

                        {/* Gateway Foods from user's history */}
                        {def.gatewayFoods.length > 0 && (
                          <div className="mt-3">
                            <div className="text-xs font-medium opacity-75">From your diet:</div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {def.gatewayFoods.map((food, j) => (
                                <span key={j} className="px-2 py-0.5 bg-white bg-opacity-50 rounded text-xs">
                                  {food}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Recommended Foods */}
                        {def.recommendedFoods.length > 0 && (
                          <div className="mt-3">
                            <div className="text-xs font-medium opacity-75">Try adding:</div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {def.recommendedFoods.map((food, j) => (
                                <span key={j} className="px-2 py-0.5 bg-white bg-opacity-50 rounded text-xs">
                                  {food}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Educational Content */}
                        {def.educationalSnippets.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-current border-opacity-20">
                            <div className="text-xs font-medium opacity-75 mb-1">Learn more:</div>
                            <p className="text-xs opacity-90">
                              {def.educationalSnippets[0].substring(0, 200)}
                              {def.educationalSnippets[0].length > 200 ? '...' : ''}
                            </p>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* AI Summary Section */}
              {activeSection === 'summary' && (
                <div className="prose max-w-none">
                  <div className="p-6 bg-purple-50 rounded-lg border border-purple-200">
                    <h4 className="text-purple-900 font-medium mb-4 flex items-center gap-2">
                      <span>🤖</span> AI-Generated Insights
                    </h4>
                    <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                      {generatedReview.summary}
                    </div>
                  </div>
                  <div className="mt-4 text-xs text-gray-500 text-center">
                    Generated {new Date(generatedReview.generatedAt).toLocaleString()} •
                    Powered by Neo4j Graph Analysis + ChromaDB Recommendations + Vertex AI
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Past Reviews */}
      {pastReviews && pastReviews.length > 0 && !generatedReview && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Past Reviews</h3>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              {pastReviews.map((review: any) => (
                <div
                  key={review.id}
                  className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">
                        {formatDate(review.period_start)} - {formatDate(review.period_end)}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {review.days_logged} days logged
                      </div>
                    </div>
                    <div className="text-sm text-gray-500">
                      {new Date(review.generated_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
